import { track } from "../../core/App/track";
import { useFormik } from 'formik';
import { useEffect, useMemo } from 'react';
import { Button, FormGroup, Input, Select, VGrid, useToasts } from 'tombac';
import { CancelIcon } from 'tombac-icons';
import { object, string } from 'yup';
import { CharCounter } from "./CharCounter";
import { usePublishSellerFormMutation } from "./dashboardApiHooks";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const validationSchema = object({
  industry: string().required('Industry is required.'),
  phone: string().max(255, 'Phone number must be less than 255 characters.'),
  message: string().required('Message is required').max(2000, 'Message must be less than 2000 characters.')
});
const ModalLicenseForm = _ref => {
  let {
    industries,
    userData,
    onRequestClose,
    onSubmitForm
  } = _ref;
  const {
    addToast
  } = useToasts();
  const industryOptions = useMemo(() => industries.map(industry => ({
    label: industry,
    value: industry
  })), [industries]);
  const {
    mutateAsync: publishSellerForm
  } = usePublishSellerFormMutation();
  const formik = useFormik({
    initialValues: {
      industry: userData ? userData.industry : '',
      phone: userData ? userData.phone : '',
      message: ''
    },
    onSubmit: values => publishSellerForm({
      values
    }).then(() => {
      track('form_submit', {
        category: 'dashboard',
        specificProps: {
          form_type: 'mp_contact_license',
          visitor_type: 'known',
          account_type: 'move',
          staticId: Buffer.from(userData.email).toString('base64')
        }
      });
      onSubmitForm();
    }).catch(() => {
      addToast('Failed to send the form, please try again', 'danger');
    }),
    validationSchema
  });
  useEffect(() => {
    if (userData) {
      formik.setFieldValue('industry', userData.industry);
      formik.setFieldValue('phone', userData.phone);
    }
  }, [userData]);
  const getFormGroupProps = name => ({
    caption: formik.touched[name] && formik.errors[name],
    danger: Boolean(formik.touched[name] && formik.errors[name])
  });
  const getInputProps = name => ({
    invalid: Boolean(formik.touched[name] && formik.errors[name]),
    name,
    ...formik.getFieldProps(name)
  });
  return /*#__PURE__*/_jsxs("div", {
    "data-sentry-component": "ModalLicenseForm",
    "data-sentry-source-file": "ModalLicenseForm.tsx",
    children: [/*#__PURE__*/_jsxs("div", {
      className: "DashboardContactModal_FormLabel",
      children: ["Upgrade or expand your product license", ' ', /*#__PURE__*/_jsx("span", {
        className: "DashboardContactModal_closeIco",
        onClick: onRequestClose,
        children: /*#__PURE__*/_jsx(CancelIcon, {
          "data-sentry-element": "CancelIcon",
          "data-sentry-source-file": "ModalLicenseForm.tsx",
          "data-sentry-element": "CancelIcon"
        })
      })]
    }), /*#__PURE__*/_jsxs("form", {
      onSubmit: formik.handleSubmit,
      children: [/*#__PURE__*/_jsx(VGrid, {
        gap: 10,
        justifyContent: "stretch",
        $w: "74sp",
        "data-sentry-element": "VGrid",
        "data-sentry-source-file": "ModalLicenseForm.tsx",
        "data-sentry-element": "VGrid",
        children: /*#__PURE__*/_jsx(FormGroup, {
          htmlFor: "industry-input",
          label: "Industry",
          className: "DashboardContactModal__label--required",
          ...getFormGroupProps('industry'),
          "data-sentry-element": "FormGroup",
          "data-sentry-source-file": "ModalLicenseForm.tsx",
          "data-sentry-element": "FormGroup",
          children: /*#__PURE__*/_jsx(Select, {
            placeholder: "Select your industry",
            options: industryOptions,
            id: "industry-input",
            ...getInputProps('industry'),
            onBlur: () => formik.setFieldTouched('industry'),
            onChange: industry => !('length' in industry) && formik.setFieldValue('industry', industry.value),
            value: industryOptions.find(o => o.value === formik.values.industry),
            "data-sentry-element": "Select",
            "data-sentry-source-file": "ModalLicenseForm.tsx",
            "data-sentry-element": "Select"
          })
        })
      }), /*#__PURE__*/_jsx(VGrid, {
        $mt: "3sp",
        justifyContent: "stretch",
        $w: "74sp",
        "data-sentry-element": "VGrid",
        "data-sentry-source-file": "ModalLicenseForm.tsx",
        "data-sentry-element": "VGrid",
        children: /*#__PURE__*/_jsx(FormGroup, {
          htmlFor: "phone-input",
          label: "Phone",
          placeholder: "Type in your phone number",
          ...getFormGroupProps('phone'),
          "data-sentry-element": "FormGroup",
          "data-sentry-source-file": "ModalLicenseForm.tsx",
          "data-sentry-element": "FormGroup",
          children: /*#__PURE__*/_jsx(Input, {
            id: "phone-input",
            ...getInputProps('phone'),
            "data-sentry-element": "Input",
            "data-sentry-source-file": "ModalLicenseForm.tsx",
            "data-sentry-element": "Input"
          })
        })
      }), /*#__PURE__*/_jsx(VGrid, {
        $mt: "3sp",
        justifyContent: "stretch",
        $w: "74sp",
        "data-sentry-element": "VGrid",
        "data-sentry-source-file": "ModalLicenseForm.tsx",
        "data-sentry-element": "VGrid",
        children: /*#__PURE__*/_jsx(FormGroup, {
          htmlFor: "message-input",
          className: "DashboardContactModal__label--required",
          label: "Message",
          ...getFormGroupProps('message'),
          "data-sentry-element": "FormGroup",
          "data-sentry-source-file": "ModalLicenseForm.tsx",
          "data-sentry-element": "FormGroup",
          children: /*#__PURE__*/_jsxs("div", {
            className: "DashboardContactForm__textareaWrapper",
            children: [/*#__PURE__*/_jsx("textarea", {
              placeholder: "Please describe your use case",
              rows: 10,
              className: "DashboardContactForm__textarea",
              id: "message-input",
              ...getInputProps('message')
            }), /*#__PURE__*/_jsx(CharCounter, {
              value: `${formik.values.message.length}/2000 `,
              "data-sentry-element": "CharCounter",
              "data-sentry-source-file": "ModalLicenseForm.tsx",
              "data-sentry-element": "CharCounter"
            })]
          })
        })
      }), /*#__PURE__*/_jsx(VGrid, {
        justifyContent: "stretch",
        $mt: "3sp",
        $w: "18sp",
        "data-sentry-element": "VGrid",
        "data-sentry-source-file": "ModalLicenseForm.tsx",
        "data-sentry-element": "VGrid",
        children: /*#__PURE__*/_jsx(Button, {
          busy: formik.isSubmitting,
          type: "submit",
          variant: "primary",
          "data-sentry-element": "Button",
          "data-sentry-source-file": "ModalLicenseForm.tsx",
          "data-sentry-element": "Button",
          children: "Send message"
        })
      })]
    })]
  });
};
export default ModalLicenseForm;